/**
 * Access process training (13 Oct): the pure helpers behind `/learn/access`.
 *
 * No React and no imports, so `node --test` runs them as they are:
 * which chapters to show (detected roles, then "I also do…"), which lanes
 * and steps of the process map light up ("you are here"), and the check
 * questions (answer, retry, first-try score, complete).
 */

export const TRAINING_ROLES = [
  "requester",
  "manager",
  "hr",
  "approver",
  "global_it",
  "backoffice",
  "crm",
  "it_admin",
] as const;

export type TrainingRoleId = (typeof TRAINING_ROLES)[number];

export interface Lane {
  id: string;
  name: string;
  short: string;
}

export interface FlowStep {
  id: string;
  lane: string;
  col: number;
  t: string;
  /** "m": by hand today; "p": named on the form only. */
  kind?: string;
}

export interface Flow {
  id: string;
  title: string;
  sub: string;
  cols: number;
  steps: FlowStep[];
}

export interface GuideStep {
  /** Light markup: `**bold**`; ①… are the screenshot's callouts. */
  text: string;
  img: string | null;
}

export interface Question {
  q: string;
  o: string[];
  /** Index of the right option. */
  a: number;
  why: string;
}

export interface Chapter {
  title: string;
  when: string;
  intro: string;
  note?: string;
  sections: { title: string; steps: GuideStep[] }[];
  lanes: string[];
  done: string;
  mistakes: string[];
  quiz: Question[];
}

export interface TrainingContent {
  version: string;
  lanes: Lane[];
  flows: Flow[];
  roles: string[];
  chapters: Record<string, Chapter>;
}

export interface RoleChoice {
  /** Roles the person added ("I also do…"). */
  added: string[];
  /** Detected roles the person took away. */
  removed: string[];
}

export const NO_CHOICE: RoleChoice = { added: [], removed: [] };

/**
 * The chapters to show, in page order: the detected roles, plus the ones
 * added, minus the ones removed. Requester is always there unless removed.
 * An unknown role id is ignored.
 */
export function chosenRoles(
  detected: readonly string[],
  choice: RoleChoice = NO_CHOICE,
): TrainingRoleId[] {
  const wanted = new Set([...detected, ...choice.added]);
  for (const role of choice.removed) {
    wanted.delete(role);
  }
  return TRAINING_ROLES.filter((role) => wanted.has(role));
}

/** Add or remove one role from the choice, keeping detected roles the default. */
export function toggleRole(
  choice: RoleChoice,
  detected: readonly string[],
  role: string,
  on: boolean,
): RoleChoice {
  const isDetected = detected.includes(role);
  const added = choice.added.filter((r) => r !== role);
  const removed = choice.removed.filter((r) => r !== role);
  if (on && !isDetected) {
    added.push(role);
  }
  if (!on && isDetected) {
    removed.push(role);
  }
  return { added, removed };
}

/** The process map's lanes the person works in. */
export function lanesFor(roles: readonly string[], content: TrainingContent): Set<string> {
  const lanes = new Set<string>();
  for (const role of roles) {
    for (const lane of content.chapters[role]?.lanes ?? []) {
      lanes.add(lane);
    }
  }
  return lanes;
}

/** Ids ("new:a") of every step on the person's lanes: "you are here". */
export function highlightedSteps(roles: readonly string[], content: TrainingContent): Set<string> {
  const lanes = lanesFor(roles, content);
  const out = new Set<string>();
  for (const flow of content.flows) {
    for (const step of flow.steps) {
      if (lanes.has(step.lane)) {
        out.add(`${flow.id}:${step.id}`);
      }
    }
  }
  return out;
}

/** The first role whose chapter covers a lane, among the person's roles (map click → chapter). */
export function roleForLane(
  lane: string,
  roles: readonly string[],
  content: TrainingContent,
): string | null {
  return roles.find((role) => content.chapters[role]?.lanes.includes(lane)) ?? null;
}

// ─── Check questions ───────────────────────────────────────────────────

/** One question's answers so far: every pick, in order. */
export type QuizState = Record<number, number[]>;

/** Record a pick. A question already answered right takes no more picks. */
export function answer(
  state: QuizState,
  index: number,
  option: number,
  quiz: Question[],
): QuizState {
  const picks = state[index] ?? [];
  if (picks.includes(quiz[index]?.a ?? -1)) {
    return state;
  }
  return { ...state, [index]: [...picks, option] };
}

export type QuestionStatus = "unanswered" | "wrong" | "right";

export function questionStatus(state: QuizState, index: number, quiz: Question[]): QuestionStatus {
  const picks = state[index] ?? [];
  if (picks.length === 0) {
    return "unanswered";
  }
  return picks.includes(quiz[index]?.a ?? -1) ? "right" : "wrong";
}

/** Complete: every question answered right (retries allowed). */
export function isComplete(state: QuizState, quiz: Question[]): boolean {
  return quiz.length > 0 && quiz.every((_, i) => questionStatus(state, i, quiz) === "right");
}

/** Right on the first pick. */
export function firstTryScore(state: QuizState, quiz: Question[]): number {
  return quiz.filter((q, i) => (state[i] ?? [])[0] === q.a).length;
}

// ─── Text ──────────────────────────────────────────────────────────────

export interface TextPart {
  text: string;
  bold?: boolean;
  callout?: boolean;
}

const CALLOUTS = "①②③④⑤⑥⑦⑧⑨⑩";

/** Split light markup into parts: `**bold**`, and ①…⑩ as callouts. */
export function parseMarkup(text: string): TextPart[] {
  const parts: TextPart[] = [];
  const pieces = text.split(/(\*\*[^*]+\*\*)/g);
  for (const piece of pieces) {
    if (piece === "") {
      continue;
    }
    if (piece.startsWith("**") && piece.endsWith("**") && piece.length > 4) {
      parts.push({ text: piece.slice(2, -2), bold: true });
      continue;
    }
    let buffer = "";
    for (const ch of piece) {
      if (CALLOUTS.includes(ch)) {
        if (buffer) {
          parts.push({ text: buffer });
          buffer = "";
        }
        parts.push({ text: ch, callout: true });
      } else {
        buffer += ch;
      }
    }
    if (buffer) {
      parts.push({ text: buffer });
    }
  }
  return parts;
}
